/**
 * Zod validation schemas for listings
 *
 * Shared between client-side forms and server-side API routes.
 * Single source of truth for listing data validation.
 */

import { z } from "zod/v4";

/** Valid listing types */
export const listingTypeEnum = z.enum(["DONATION", "LOAN", "SALE"]);

/** Valid item conditions */
export const itemConditionEnum = z.enum([
    "NEW",
    "LIKE_NEW",
    "GOOD",
    "FAIR",
    "WORN",
]);

/** Valid listing statuses */
export const listingStatusEnum = z.enum([
    "DRAFT",
    "ACTIVE",
    "RESERVED",
    "COMPLETED",
    "EXPIRED",
    "ARCHIVED",
]);

/** Base listing fields (without cross-field refinement) */
export const createListingBaseSchema = z.object({
    title: z
        .string()
        .min(5, "Le titre doit contenir au moins 5 caractères")
        .max(120, "Le titre ne doit pas dépasser 120 caractères"),
    description: z
        .string()
        .min(20, "La description doit contenir au moins 20 caractères")
        .max(5000, "La description ne doit pas dépasser 5000 caractères"),
    type: listingTypeEnum,
    condition: itemConditionEnum,
    price: z
        .number()
        .min(0, "Le prix doit être positif")
        .max(10000, "Le prix maximum est 10 000 €")
        .optional(),
    deposit: z
        .number()
        .min(0)
        .max(5000, "La caution maximum est 5 000 €")
        .optional(),
    loanDuration: z.string().optional(),
    categoryId: z.string().min(1, "Veuillez sélectionner une catégorie"),
    city: z
        .string()
        .min(2, "Veuillez indiquer une ville")
        .max(100),
    postalCode: z
        .string()
        .regex(/^\d{5}$/, "Le code postal doit contenir 5 chiffres"),
});

/** Schema for creating a new listing (with cross-field validation) */
export const createListingSchema = createListingBaseSchema.refine(
    (data) => {
        if (data.type === "SALE" && (data.price === undefined || data.price <= 0)) {
            return false;
        }
        return true;
    },
    {
        message: "Un prix est requis pour une vente",
        path: ["price"],
    }
);

/** Schema for updating a listing (includes status changes) */
export const updateListingSchema = createListingBaseSchema.partial().extend({
    status: listingStatusEnum.optional(),
});

/** Schema for search/filter */
export const searchListingsSchema = z.object({
    query: z.string().optional(),
    type: listingTypeEnum.optional(),
    condition: itemConditionEnum.optional(),
    categoryId: z.string().optional(),
    postalCode: z.string().optional(),
    maxPrice: z.coerce.number().optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.enum(["createdAt", "price", "title"]).default("createdAt"),
    sortOrder: z.enum(["asc", "desc"]).default("desc"),
});

/** TypeScript types inferred from schemas */
export type CreateListingInput = z.infer<typeof createListingBaseSchema>;
export type UpdateListingInput = z.infer<typeof updateListingSchema>;
export type SearchListingsInput = z.infer<typeof searchListingsSchema>;
